import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { VerifiedUser as VerifiedUserIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { MaintenanceRecord } from '../../../../../types/api';
import { AttachmentList } from './AttachmentList';
import { useDeleteMaintenanceRecordMutation } from '../../maintenance.mutations';
import { useTranslation } from '../../../../../i18n';
import { useAuthV2 } from '../../../../../contexts/AuthContextV2';
import { canManageMaintenance as checkCanManageMaintenance } from '../../../../../config/roles';
import { formatDate, prettifyMaintenanceType } from '../../../../../utils/maintenanceTypeUtils';

interface ViewHistoricalMaintenanceDialogProps {
  open: boolean;
  record: MaintenanceRecord | null;
  onClose: () => void;
}

export const ViewHistoricalMaintenanceDialog: React.FC<ViewHistoricalMaintenanceDialogProps> = ({
  open,
  record,
  onClose,
}) => {
  const { t } = useTranslation();
  const { profile } = useAuthV2();
  const canManageMaintenance = checkCanManageMaintenance(profile?.organization?.roles);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const deleteMaintenanceRecordMutation = useDeleteMaintenanceRecordMutation();

  if (!record) return null;

  const isMultiDay = record.start_date !== record.end_date && record.end_date;
  const displayDate = record.end_date || record.start_date;

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!record?.id) return;

    deleteMaintenanceRecordMutation.mutate(
      { recordId: record.id, droneSerial: record.drone_serial },
      {
        onSuccess: () => {
          setSnackbarMessage(t('maintenanceTab.deleteSuccess'));
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          setDeleteConfirmOpen(false);

          // Close the dialog after successful deletion
          setTimeout(() => {
            onClose();
          }, 500);
        },
        onError: (err: unknown) => {
          console.error('Error deleting maintenance record:', err);
          setSnackbarMessage(err instanceof Error ? err.message : t('maintenanceTab.deleteFailed'));
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        },
      }
    );
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ pt: 3 }}>
        {/* Header with maintenance type and status */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {prettifyMaintenanceType(record.maintenance_type)}
            </Typography>
            <Chip label={record.status} size="small" color="success" sx={{ ml: 'auto' }} />
          </Box>

          <Typography variant="body2" color="text.secondary">
            {isMultiDay
              ? `${formatDate(record.start_date)} - ${formatDate(record.end_date)}`
              : formatDate(displayDate)}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Work Description */}
        {record.work_description && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('maintenanceTab.workDescription')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {record.work_description}
            </Typography>
          </Box>
        )}

        {/* Odoo Work Order ID */}
        {record.odoo_work_order_id && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('maintenanceTab.workOrderId')}</strong> {record.odoo_work_order_id}
            </Typography>
          </Box>
        )}

        {/* Performed by information */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('maintenanceTab.performedBy')}</strong>{' '}
            {record.performed_by_name || record.performed_by || t('maintenanceTab.unknown')}
          </Typography>
        </Box>

        {/* Quality check information */}
        {record.quality_checked && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUserIcon fontSize="small" color="primary" />
            <Typography variant="body2" color="text.secondary">
              <strong>{t('maintenanceTab.qualityCheckedBy')}</strong>{' '}
              {record.quality_checked_by_name || t('maintenanceTab.unknown')}
            </Typography>
          </Box>
        )}

        {/* Attachments */}
        {record.attachments &&
          Array.isArray(record.attachments) &&
          record.attachments.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <AttachmentList
                attachments={
                  record.attachments as unknown as Array<{
                    name: string;
                    url: string;
                    key?: string;
                    type?: string;
                  }>
                }
              />
            </Box>
          )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        {canManageMaintenance ? (
          <Button
            onClick={handleDeleteClick}
            startIcon={<DeleteIcon />}
            color="error"
            disabled={deleteMaintenanceRecordMutation.isPending}
          >
            {t('common.delete')}
          </Button>
        ) : (
          <Box />
        )}
        <Button onClick={onClose} variant="contained">
          {t('common.close')}
        </Button>
      </DialogActions>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('maintenanceTab.deleteMaintenanceRecord')}</DialogTitle>
        <DialogContent>
          <Typography>{t('maintenanceTab.deleteConfirmMessage')}</Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {prettifyMaintenanceType(record.maintenance_type)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(displayDate)}
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('maintenanceTab.deleteRecalculationWarning')} <strong>{record.drone_serial}</strong>.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteMaintenanceRecordMutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMaintenanceRecordMutation.isPending}
          >
            {deleteMaintenanceRecordMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('common.delete')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
